<template>
  <div class="w-100 h-100" style="margin: 0 !important; padding: 0 !important">
    <div
      class="d-flex flex-row position-absolute top-0 right-0 mt-8 me-4 me-md-8 font-weight-bold text-purple-4 ga-4"
    >
      <a
        v-if="canInvite && isInternalInviteEnabled"
        class="cursor-pointer"
        style="z-index: 1000"
        @click="handleInviteClick"
      >
        {{ t("onboarding.join.inviteButton") }} <v-icon>mdi-email-plus-outline</v-icon>
      </a>
      <a
        v-if="onboardingStore.draftMerchant"
        class="cursor-pointer"
        style="z-index: 1000"
        @click="handleExitOnboardingClick"
      >
        {{ t("onboarding.join.exitButton" + (canInvite && xs ? "Mobile" : "")) }}
        <v-icon>mdi-logout</v-icon>
      </a>
    </div>
    <div class="w-100 h-100">
      <component :is="componentForStatus" />
    </div>
  </div>
  <onboarding-internal-invite-modal v-model="inviteDialog" />
</template>

<script setup lang="ts">
import { DraftMerchantStatusEnum } from "@tableyeti/merchant-service";
import { PostHog } from "posthog-js";
import { computed, inject, onBeforeMount, ref } from "vue";
import type { Component } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import OnboardingAuth from "@/components/Onboarding/OnboardingAuth.vue";
import OnboardingDetails from "@/components/Onboarding/OnboardingDetails.vue";
import OnboardingInvite from "@/components/Onboarding/OnboardingInvite.vue";
import OnboardingKYCStatus from "@/components/Onboarding/OnboardingKYCStatus.vue";
import OnboardingSignUp from "@/components/Onboarding/OnboardingSignUp.vue";
import OnboardingSiteDevice from "@/components/Onboarding/OnboardingSiteDevice.vue";
import { OnboardingFlowStatusEnum, OnboardingStatus, useOnboardingStore } from "@/store/onboarding";

const { t } = useI18n();

const onboardingStore = useOnboardingStore();
const route = useRoute();
const { xs } = useDisplay();

const inviteDialog = ref<boolean>(false);

const posthog = inject<PostHog>("posthog");

const isInternalInviteEnabled = posthog?.isFeatureEnabled("onboarding-internal-invite");

onBeforeMount(async () => {
  await onboardingStore.initialize();

  if (
    route.query.invite &&
    onboardingStore.draftMerchant &&
    onboardingStore.inviteToken !== route.query.invite
  ) {
    onboardingStore.clearDraftMerchant();
  }
});

const componentForStatus = computed(() => {
  if (onboardingStore.isInitialising()) {
    //TODO(stefano-yeti): Show a nice loader here
    return null;
  }

  if (route.query.invite || onboardingStore.inviteToken) {
    return OnboardingInvite;
  }

  const draftMerchant = onboardingStore.draftMerchant;
  if (!draftMerchant) {
    return OnboardingAuth;
  }

  if (route.query.redirect && route.query.redirect_status === "succeeded") {
    onboardingStore.setStatusOverride(OnboardingFlowStatusEnum.SiteEdit);
  }

  const statusComponentMap: { [status in OnboardingStatus]?: Component } = {
    [DraftMerchantStatusEnum.Created]: OnboardingDetails,
    [DraftMerchantStatusEnum.DetailsAdded]: OnboardingDetails,
    [DraftMerchantStatusEnum.DetailsSubmittedError]: OnboardingDetails,
    [DraftMerchantStatusEnum.DetailsSubmitted]: OnboardingKYCStatus,
    [DraftMerchantStatusEnum.SiteAdded]: OnboardingKYCStatus,
    [OnboardingFlowStatusEnum.SiteEdit]: OnboardingSiteDevice,
    [DraftMerchantStatusEnum.SiteSubmittedError]: OnboardingKYCStatus,
    [DraftMerchantStatusEnum.SiteSubmitted]: OnboardingKYCStatus,
    [OnboardingFlowStatusEnum.SignUp]: OnboardingSignUp,
  };

  return statusComponentMap[onboardingStore.onboardingStatus] || OnboardingAuth;
});

const canInvite = computed(() => {
  return (
    onboardingStore.draftMerchant?.email?.endsWith("@yetipay.me") &&
    onboardingStore.draftMerchant?.status === DraftMerchantStatusEnum.SiteSubmitted &&
    (componentForStatus.value === OnboardingKYCStatus ||
      componentForStatus.value === OnboardingSignUp)
  );
});

const handleExitOnboardingClick = () => {
  onboardingStore.clearDraftMerchant();
};

const handleInviteClick = () => {
  inviteDialog.value = true;
};
</script>
